<div class="payments-page">
  <!-- Page Header -->
  <div class="payments-header">
    <div class="backButton" (click)="goBack()">
      <img src="/assets/icons/arrow-left.svg" alt="Go back" />
    </div>
    <p class="page-title">{{ 'Payments' | translate }}</p>
    <p class="page-subtitle">{{ 'Configure how your customers pay for orders.' | translate }}</p>
  </div>

  <!-- Payment Grid -->
  <form [formGroup]="paymentSettingsForm" id="paymentSettingsForm" (ngSubmit)="saveSettings()">
    <div class="payment-grid">

      <!-- Main Payment Methods -->
      <ng-container *ngFor="let payment of paymentMethods">
        <div
          class="payment-card-wrapper"
          *ngIf="(payment.id === 'cod' || payment.id === 'stripe' || payment.id === 'geidea' || payment.id === 'paypal' || payment.id === 'dineropay' || payment.id === 'paymob' || payment.id === 'nomod') && isMethodAvailableInCountry(payment.id)"
          [class.active]="payment.checked">

          <button
            type="button"
            class="payment-card-btn"
            (click)="togglePayment(payment)">
            <div class="card-left">
              <span class="icon-wrapper">
                <img
                  loading="lazy"
                  [src]="getPaymentIcon(payment.id, payment.checked)"
                  class="payment-icon"
                  alt="{{ payment.title }} icon" />
              </span>
              <span class="payment-label">{{ payment.title }}</span>
            </div>
            <!-- Connected state needs no badge — the card's own active/blue-border
                 styling (payment.checked, kept in sync with the live status) already
                 says that. Only surface a badge when something still needs attention. -->
            <span
              class="stripe-status-badge action-required"
              *ngIf="payment.id === 'stripe' && !isStripeConnectFullyActive && stripeConnectStatus?.onboardingStatus && stripeConnectStatus?.onboardingStatus !== 'not_started'">
              {{ 'Action required' | translate }}
            </span>
            <!-- Connected and charging fine, but Stripe will pause payouts unless the
                 merchant provides an eventually_due requirement (see payoutsAtRisk). -->
            <span
              class="stripe-status-badge payouts-at-risk"
              *ngIf="payment.id === 'stripe' && isStripeConnectFullyActive && stripeConnectStatus?.payoutsAtRisk">
              {{ 'Payouts paused soon' | translate }}
            </span>
          </button>

          <!-- Expanded Inputs (Stripe is configured via a popup instead) -->
          <div *ngIf="payment.checked && payment.id !== 'cod' && payment.id !== 'stripe'" class="card-expanded">

            <!-- Geidea -->
            <ng-container *ngIf="payment.id === 'geidea'">
              <div class="field-group">
                <input
                  class="field-input"
                  formControlName="geideaPublicKey"
                  placeholder="{{ 'Merchant public key' | translate }}"
                  [class.has-error]="paymentSettingsForm.get('geideaPublicKey')?.invalid && (paymentSettingsForm.get('geideaPublicKey')?.touched || formSubmitted)" />
                <span class="field-error" *ngIf="paymentSettingsForm.get('geideaPublicKey')?.hasError('required') && (paymentSettingsForm.get('geideaPublicKey')?.touched || formSubmitted)">
                  {{ 'Field required' | translate }}
                </span>
                <span class="field-error" *ngIf="paymentSettingsForm.get('geideaPublicKey')?.hasError('invalidPublicKey')">
                  {{ invalidGeideaKey }}
                </span>
              </div>
              <div class="field-group">
                <input
                  class="field-input"
                  formControlName="geideaApiPassword"
                  placeholder="{{ 'API password' | translate }}"
                  [class.has-error]="paymentSettingsForm.get('geideaApiPassword')?.invalid && (paymentSettingsForm.get('geideaApiPassword')?.touched || formSubmitted)" />
                <span class="field-error" *ngIf="paymentSettingsForm.get('geideaApiPassword')?.hasError('required') && (paymentSettingsForm.get('geideaApiPassword')?.touched || formSubmitted)">
                  {{ 'Field required' | translate }}
                </span>
                <span class="field-error" *ngIf="paymentSettingsForm.get('geideaApiPassword')?.hasError('invalidPassword')">
                  {{ invalidGeideaKey }}
                </span>
              </div>
            </ng-container>

            <!-- PayPal -->
            <ng-container *ngIf="payment.id === 'paypal'">
              <div class="field-group">
                <input
                  class="field-input"
                  formControlName="paypalClientId"
                  placeholder="{{ 'Merchant Client ID' | translate }}"
                  [class.has-error]="paymentSettingsForm.get('paypalClientId')?.invalid && (paymentSettingsForm.get('paypalClientId')?.touched || formSubmitted)" />
                <span class="field-error" *ngIf="paymentSettingsForm.get('paypalClientId')?.hasError('required') && (paymentSettingsForm.get('paypalClientId')?.touched || formSubmitted)">
                  {{ 'Field required' | translate }}
                </span>
                <span class="field-error" *ngIf="paymentSettingsForm.get('paypalClientId')?.hasError('invalidClientId')">
                  {{ invalidPaypalKey }}
                </span>
              </div>
              <div class="field-group">
                <input
                  class="field-input"
                  formControlName="paypalSecretKey"
                  placeholder="{{ 'Merchant Secret key' | translate }}"
                  [class.has-error]="paymentSettingsForm.get('paypalSecretKey')?.invalid && (paymentSettingsForm.get('paypalSecretKey')?.touched || formSubmitted)" />
                <span class="field-error" *ngIf="paymentSettingsForm.get('paypalSecretKey')?.hasError('required') && (paymentSettingsForm.get('paypalSecretKey')?.touched || formSubmitted)">
                  {{ 'Field required' | translate }}
                </span>
                <span class="field-error" *ngIf="paymentSettingsForm.get('paypalSecretKey')?.hasError('invalidSecretKey')">
                  {{ invalidPaypalKey }}
                </span>
              </div>
            </ng-container>

            <!-- DineroPay -->
            <ng-container *ngIf="payment.id === 'dineropay'">
              <div class="field-group">
                <input
                  class="field-input"
                  formControlName="dineropayMerchantKey"
                  placeholder="{{ 'Merchant Key' | translate }}"
                  [class.has-error]="paymentSettingsForm.get('dineropayMerchantKey')?.invalid && (paymentSettingsForm.get('dineropayMerchantKey')?.touched || formSubmitted)" />
                <span class="field-error" *ngIf="paymentSettingsForm.get('dineropayMerchantKey')?.hasError('required') && (paymentSettingsForm.get('dineropayMerchantKey')?.touched || formSubmitted)">
                  {{ 'Field required' | translate }}
                </span>
              </div>
              <div class="field-group">
                <input
                  class="field-input"
                  type="password"
                  formControlName="dineropayMerchantPassword"
                  placeholder="{{ 'Merchant Password' | translate }}"
                  [class.has-error]="paymentSettingsForm.get('dineropayMerchantPassword')?.invalid && (paymentSettingsForm.get('dineropayMerchantPassword')?.touched || formSubmitted)" />
                <span class="field-error" *ngIf="paymentSettingsForm.get('dineropayMerchantPassword')?.hasError('required') && (paymentSettingsForm.get('dineropayMerchantPassword')?.touched || formSubmitted)">
                  {{ 'Field required' | translate }}
                </span>
              </div>
            </ng-container>

            <!-- Paymob -->
            <ng-container *ngIf="payment.id === 'paymob'">
              <div class="field-group">
                <input
                  class="field-input"
                  formControlName="paymobPublicKey"
                  placeholder="{{ 'Paymob Public Key' | translate }}"
                  [class.has-error]="paymentSettingsForm.get('paymobPublicKey')?.invalid && (paymentSettingsForm.get('paymobPublicKey')?.touched || formSubmitted)" />
                <span class="field-error" *ngIf="paymentSettingsForm.get('paymobPublicKey')?.hasError('required') && (paymentSettingsForm.get('paymobPublicKey')?.touched || formSubmitted)">
                  {{ 'Public Key required' | translate }}
                </span>
              </div>
              <div class="field-group">
                <input
                  class="field-input"
                  formControlName="paymobSecretKey"
                  placeholder="{{ 'Paymob Secret Key' | translate }}"
                  [class.has-error]="paymentSettingsForm.get('paymobSecretKey')?.invalid && (paymentSettingsForm.get('paymobSecretKey')?.touched || formSubmitted)" />
                <span class="field-error" *ngIf="paymentSettingsForm.get('paymobSecretKey')?.hasError('required') && (paymentSettingsForm.get('paymobSecretKey')?.touched || formSubmitted)">
                  {{ 'Secret Key required' | translate }}
                </span>
              </div>
              <div class="field-group">
                <select
                  class="field-input field-select"
                  formControlName="paymobCountry"
                  [class.has-error]="paymentSettingsForm.get('paymobCountry')?.invalid && (paymentSettingsForm.get('paymobCountry')?.touched || formSubmitted)">
                  <option value="" disabled>{{ 'Paymob Country' | translate }}</option>
                  <option *ngFor="let c of paymobCountries" [value]="c.code">{{ c.name }}</option>
                </select>
                <span class="field-error" *ngIf="paymentSettingsForm.get('paymobCountry')?.hasError('required') && (paymentSettingsForm.get('paymobCountry')?.touched || formSubmitted)">
                  {{ 'Country is required' | translate }}
                </span>
              </div>
            </ng-container>

            <!-- Nomod -->
            <ng-container *ngIf="payment.id === 'nomod'">
              <div class="field-group">
                <input
                  class="field-input"
                  formControlName="nomodApiKey"
                  placeholder="{{ 'Nomod API Key' | translate }}"
                  [class.has-error]="paymentSettingsForm.get('nomodApiKey')?.invalid && (paymentSettingsForm.get('nomodApiKey')?.touched || formSubmitted)" />
                <span class="field-error" *ngIf="paymentSettingsForm.get('nomodApiKey')?.hasError('required') && (paymentSettingsForm.get('nomodApiKey')?.touched || formSubmitted)">
                  {{ 'Field required' | translate }}
                </span>
              </div>
            </ng-container>

          </div>
        </div>
      </ng-container>

      <!-- Paymob Sub-Methods -->
      <ng-container *ngIf="isPaymobChecked && arePaymobFieldsFilled()">
        <ng-container *ngFor="let payment of paymobSubMethods">
          <div
            class="payment-card-wrapper"
            [class.active]="payment.checked">

            <button
              type="button"
              class="payment-card-btn"
              (click)="togglePayment(payment)">
              <div class="card-left">
                <span class="icon-wrapper">
                  <img
                    loading="lazy"
                    [src]="getPaymentIcon(payment.id, payment.checked)"
                    class="payment-icon"
                    alt="{{ payment.title }} icon" />
                </span>
                <span class="payment-label">{{ payment.title }}</span>
              </div>
            </button>

            <div *ngIf="payment.checked" class="card-expanded">
              <div class="field-group">
                <input
                  class="field-input"
                  [formControlName]="getControlName(payment.id)"
                  placeholder="{{ payment.description }} Payment ID"
                  [class.has-error]="paymentSettingsForm.get(getControlName(payment.id))?.invalid && (paymentSettingsForm.get(getControlName(payment.id))?.touched || formSubmitted)"
                  (ngModelChange)="updatePaymentId(payment, $event)" />
                <span class="field-error" *ngIf="paymentSettingsForm.get(getControlName(payment.id))?.hasError('required') && (paymentSettingsForm.get(getControlName(payment.id))?.touched || formSubmitted)">
                  {{ 'Field required' | translate }}
                </span>
              </div>
            </div>
          </div>
        </ng-container>
      </ng-container>

    </div>
  </form>
</div>
